/**
 * GIWAHS — Admin Memberships Console — Step 11
 * ---------------------------------------------------------------------
 * Admin-only. Reads aggregate allocation + waitlist state and exposes
 * the operational controls a founder operator needs while running the
 * Founding Partner programme:
 *
 *   Tabs:
 *     1. Overview     — per-wave + per-tier capacity table
 *     2. Waitlist     — FIFO queue · promote a position
 *     3. Allocations  — rebalance ±delta of public_capacity per wave/tier
 *     4. Audit        — read-only stream of membership_events
 *
 * Mutations route through membershipsClient.admin.* → backend admin
 * endpoints (which are themselves only mounted when MEMBERSHIPS_ENABLED).
 * The DB-side admin RPCs (Migration 020) are AUTHORED ONLY for this RC
 * — applying them remains a founder-approved gate.
 */
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { membershipsClient, isMembershipsUiEnabled, TIER_META } from '../lib/membershipsClient';

const GOLD = '#9A7B2A';
const TABS = [
  { id: 'overview',    label: 'Overview' },
  { id: 'waitlist',    label: 'Waitlist' },
  { id: 'allocations', label: 'Allocations' },
  { id: 'audit',       label: 'Audit' },
];

const NumberCell = ({ n }) => <span className="font-mono text-neutral-900">{Number(n ?? 0).toLocaleString('en-US')}</span>;

const ComingSoon = ({ reason }) => (
  <section data-testid="admin-memberships-coming-soon" className="mx-auto max-w-3xl px-5 py-24 text-center md:py-32">
    <div className="label-overline" style={{ color: GOLD }}>/ Admin · Memberships · pre-launch</div>
    <h1 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2rem,5vw,3rem)', lineHeight: 0.95 }}>
      Admin console activates with the programme.
    </h1>
    <p className="mt-6 text-neutral-700 text-lg leading-relaxed">The memberships router is currently disabled. This page becomes operational the moment <code className="font-mono">MEMBERSHIPS_ENABLED=true</code>.</p>
    <p className="mt-2 text-[11px] uppercase tracking-wider text-neutral-400 font-mono">Mode: {reason || 'pre-launch'}</p>
  </section>
);

const AdminMemberships = () => {
  const [tab, setTab]       = useState('overview');
  const [state, setState]   = useState({ status: 'loading' });
  const [busy, setBusy]     = useState(null);
  const [waitlistFilter, setWaitlistFilter] = useState('');

  const reload = async () => {
    setState({ status: 'loading' });
    const r = await membershipsClient.admin.overview();
    if (r?.disabled) return setState({ status: 'disabled', reason: r.reason });
    if (r?.code === 'forbidden') return setState({ status: 'forbidden' });
    if (!r?.ok) return setState({ status: 'error', error: r?.detail || 'Could not load admin data.' });
    setState({ status: 'ready', overview: r });
  };

  useEffect(() => {
    if (!isMembershipsUiEnabled()) {
      setState({ status: 'disabled', reason: 'ui_flag_off' });
      return;
    }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Waitlist tab (lazy-load on demand)
  const [waitlist, setWaitlist] = useState({ status: 'idle', rows: [] });
  const loadWaitlist = async () => {
    setWaitlist({ status: 'loading', rows: [] });
    const r = await membershipsClient.admin.waitlist({ tier: waitlistFilter || undefined });
    if (r?.ok && Array.isArray(r.rows)) setWaitlist({ status: 'ready', rows: r.rows });
    else setWaitlist({ status: 'error', rows: [], error: r?.detail || 'Failed to load waitlist.' });
  };
  useEffect(() => { if (tab === 'waitlist' && state.status === 'ready') loadWaitlist(); /* eslint-disable-next-line */ }, [tab, waitlistFilter, state.status]);

  const onPromote = async (waitlistId) => {
    setBusy(`promote-${waitlistId}`);
    try {
      const r = await membershipsClient.admin.promoteFromWaitlist({ waitlist_id: waitlistId });
      if (r?.ok) { toast.success('Promoted from waitlist — availability notification queued.'); loadWaitlist(); }
      else toast.error(r?.detail || 'Could not promote.');
    } finally { setBusy(null); }
  };

  const onRebalance = async (wave, tier, delta) => {
    if (!Number.isFinite(delta) || delta === 0) return;
    const ok = window.confirm(`Apply ${delta > 0 ? '+' : ''}${delta} seats to Wave ${wave} ${TIER_META[tier]?.label}?`);
    if (!ok) return;
    setBusy(`rebalance-${wave}-${tier}`);
    try {
      const r = await membershipsClient.admin.rebalanceAllocations({ wave, tier, delta });
      if (r?.ok) { toast.success('Rebalance applied.'); reload(); }
      else toast.error(r?.detail || 'Could not rebalance.');
    } finally { setBusy(null); }
  };

  if (state.status === 'loading') {
    return (
      <div data-testid="admin-memberships-loading" className="mx-auto max-w-6xl px-5 py-32 text-center">
        <div className="label-overline text-neutral-500">Loading admin console…</div>
        <div className="mt-3 h-px w-24 mx-auto bg-neutral-300 animate-pulse" />
      </div>
    );
  }
  if (state.status === 'disabled') return <ComingSoon reason={state.reason} />;
  if (state.status === 'forbidden') {
    return (
      <div data-testid="admin-memberships-forbidden" className="mx-auto max-w-3xl px-5 py-24 text-center">
        <div className="label-overline">/ Admin · 403</div>
        <h1 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2rem,5vw,3rem)' }}>You are not an admin.</h1>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div data-testid="admin-memberships-error" className="mx-auto max-w-3xl px-5 py-24 text-center">
        <div className="label-overline">/ Admin · error</div>
        <p className="mt-4 text-neutral-700">{state.error}</p>
      </div>
    );
  }

  const overview      = state.overview || {};
  const waves         = overview.waves || [];
  const events        = overview.recent_events || [];
  const totalsByWave  = waves.map(w => ({
    wave: w.wave,
    is_open: w.is_open,
    public_capacity: w.tiers.reduce((s, t) => s + Number(t.public_capacity || 0), 0),
    claimed_count:   w.tiers.reduce((s, t) => s + Number(t.claimed_count || 0), 0),
    reserved:        w.tiers.reduce((s, t) => s + Number(t.reserved_for_upgrades || 0), 0),
  }));

  return (
    <>
      <Helmet><title>Admin · Memberships · GIWAHS</title></Helmet>

      <section className="mx-auto max-w-6xl px-5 py-10" data-testid="admin-memberships">
        <div className="label-overline" style={{ color: GOLD }}>/ Admin · Memberships</div>
        <h1 className="mt-3 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.25rem,5vw,3rem)', lineHeight: 0.95 }}>
          Founding Partner programme
        </h1>

        {/* Tab nav */}
        <nav data-testid="admin-tabs" className="mt-8 flex border-b border-neutral-900">
          {TABS.map(t => (
            <button key={t.id} type="button" data-testid={`admin-tab-${t.id}`}
                    onClick={() => setTab(t.id)}
                    className={`px-4 py-2 text-xs uppercase tracking-wider font-mono ${tab === t.id ? 'bg-neutral-900 text-white' : 'bg-white text-neutral-700 hover:text-neutral-900'}`}>
              {t.label}
            </button>
          ))}
        </nav>

        {/* OVERVIEW */}
        {tab === 'overview' && (
          <div data-testid="admin-overview" className="mt-6 space-y-8">
            <div className="grid gap-4 md:grid-cols-3">
              {totalsByWave.map(w => (
                <div key={w.wave} className="border border-neutral-900 bg-white p-5">
                  <div className="flex items-center justify-between">
                    <div className="label-overline">Wave {w.wave}</div>
                    <div className={`label-overline ${w.is_open ? 'text-neutral-900' : 'text-neutral-400'}`}>{w.is_open ? '● Open' : '○ Locked'}</div>
                  </div>
                  <div className="mt-3 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: '2.25rem', lineHeight: 1 }}>
                    {w.claimed_count} / {w.public_capacity}
                  </div>
                  <div className="mt-1 font-mono text-[11px] uppercase tracking-wider text-neutral-500">
                    {w.reserved} reserved upgrades
                  </div>
                </div>
              ))}
            </div>

            <div className="overflow-x-auto border border-neutral-200">
              <table className="min-w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-mono uppercase tracking-wider text-[11px] text-neutral-500">Wave</th>
                    <th className="px-3 py-2 text-left font-mono uppercase tracking-wider text-[11px] text-neutral-500">Tier</th>
                    <th className="px-3 py-2 text-right font-mono uppercase tracking-wider text-[11px] text-neutral-500">Public capacity</th>
                    <th className="px-3 py-2 text-right font-mono uppercase tracking-wider text-[11px] text-neutral-500">Claimed</th>
                    <th className="px-3 py-2 text-right font-mono uppercase tracking-wider text-[11px] text-neutral-500">Reserved upgrades</th>
                    <th className="px-3 py-2 text-right font-mono uppercase tracking-wider text-[11px] text-neutral-500">Open?</th>
                  </tr>
                </thead>
                <tbody>
                  {waves.flatMap(w => (w.tiers || []).map(t => (
                    <tr key={`${w.wave}-${t.tier}`} className="border-b border-neutral-100">
                      <td className="px-3 py-2 font-mono text-neutral-700">{w.wave}</td>
                      <td className="px-3 py-2 text-neutral-900">{TIER_META[t.tier]?.label || t.tier}</td>
                      <td className="px-3 py-2 text-right"><NumberCell n={t.public_capacity} /></td>
                      <td className="px-3 py-2 text-right"><NumberCell n={t.claimed_count} /></td>
                      <td className="px-3 py-2 text-right"><NumberCell n={t.reserved_for_upgrades} /></td>
                      <td className="px-3 py-2 text-right font-mono text-[11px] uppercase tracking-wider">{w.is_open ? 'open' : 'locked'}</td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* WAITLIST */}
        {tab === 'waitlist' && (
          <div data-testid="admin-waitlist" className="mt-6">
            <div className="flex items-center gap-3">
              <label className="font-mono text-[11px] uppercase tracking-wider text-neutral-600">Tier filter</label>
              <select data-testid="admin-waitlist-filter" value={waitlistFilter} onChange={(e) => setWaitlistFilter(e.target.value)}
                      className="border border-neutral-900 bg-white px-2 py-1 text-sm">
                <option value="">All</option>
                {Object.entries(TIER_META).filter(([_, m]) => m.founder).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
              </select>
              <button type="button" onClick={loadWaitlist} className="ml-auto px-3 py-1.5 text-xs uppercase tracking-wider font-mono border border-neutral-900 bg-white">Refresh</button>
            </div>

            {waitlist.status === 'loading' && <div className="mt-6 text-sm text-neutral-500">Loading…</div>}
            {waitlist.status === 'error' && <div className="mt-6 text-sm text-red-700">{waitlist.error}</div>}
            {waitlist.status === 'ready' && waitlist.rows.length === 0 && (
              <div className="mt-6 border border-dashed border-neutral-300 p-6 text-center text-neutral-500 text-sm">No waitlist entries.</div>
            )}
            {waitlist.status === 'ready' && waitlist.rows.length > 0 && (
              <div className="mt-6 overflow-x-auto border border-neutral-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-neutral-50 border-b border-neutral-200">
                    <tr>
                      <th className="px-3 py-2 text-left font-mono uppercase tracking-wider text-[11px] text-neutral-500">#</th>
                      <th className="px-3 py-2 text-left font-mono uppercase tracking-wider text-[11px] text-neutral-500">Organisation</th>
                      <th className="px-3 py-2 text-left font-mono uppercase tracking-wider text-[11px] text-neutral-500">Tier</th>
                      <th className="px-3 py-2 text-left font-mono uppercase tracking-wider text-[11px] text-neutral-500">Joined</th>
                      <th className="px-3 py-2 text-right font-mono uppercase tracking-wider text-[11px] text-neutral-500">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waitlist.rows.map((r, i) => (
                      <tr key={r.id} data-testid={`admin-waitlist-row-${r.id}`} className="border-b border-neutral-100">
                        <td className="px-3 py-2 font-mono text-neutral-700">{r.queue_position ?? i + 1}</td>
                        <td className="px-3 py-2 text-neutral-900">{r.organisation_name || '—'}</td>
                        <td className="px-3 py-2 text-neutral-700">{TIER_META[r.tier]?.label || r.tier}</td>
                        <td className="px-3 py-2 font-mono text-[11px] text-neutral-500">{(r.joined_at || '').slice(0, 16).replace('T', ' ')}</td>
                        <td className="px-3 py-2 text-right">
                          <button type="button" data-testid={`admin-waitlist-promote-${r.id}`}
                                  disabled={busy === `promote-${r.id}`} onClick={() => onPromote(r.id)}
                                  className="px-3 py-1.5 text-xs uppercase tracking-wider font-mono bg-neutral-900 text-white disabled:opacity-40">
                            {busy === `promote-${r.id}` ? 'Working…' : 'Promote →'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ALLOCATIONS — rebalance */}
        {tab === 'allocations' && (
          <div data-testid="admin-allocations" className="mt-6 space-y-6">
            <p className="text-sm text-neutral-700">Adjust public capacity per wave/tier. Positive deltas add seats; negative deltas reclaim seats (only if unclaimed).</p>
            {waves.map(w => (
              <div key={w.wave} className="border border-neutral-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="label-overline">Wave {w.wave}</div>
                  <div className={`label-overline ${w.is_open ? 'text-neutral-900' : 'text-neutral-400'}`}>{w.is_open ? '● Open' : '○ Locked'}</div>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-3">
                  {(w.tiers || []).map(t => (
                    <RebalanceCell key={t.tier} wave={w.wave} tier={t.tier} row={t} onApply={onRebalance} busyKey={`rebalance-${w.wave}-${t.tier}`} busy={busy} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* AUDIT */}
        {tab === 'audit' && (
          <div data-testid="admin-audit" className="mt-6">
            <div className="overflow-x-auto border border-neutral-200">
              <table className="min-w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-mono uppercase tracking-wider text-[11px] text-neutral-500">When</th>
                    <th className="px-3 py-2 text-left font-mono uppercase tracking-wider text-[11px] text-neutral-500">Event</th>
                    <th className="px-3 py-2 text-left font-mono uppercase tracking-wider text-[11px] text-neutral-500">Organisation</th>
                    <th className="px-3 py-2 text-left font-mono uppercase tracking-wider text-[11px] text-neutral-500">Tier</th>
                    <th className="px-3 py-2 text-left font-mono uppercase tracking-wider text-[11px] text-neutral-500">Actor</th>
                  </tr>
                </thead>
                <tbody>
                  {events.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-neutral-500 text-sm">No events yet.</td></tr>
                  )}
                  {events.map(ev => (
                    <tr key={ev.id} className="border-b border-neutral-100">
                      <td className="px-3 py-2 font-mono text-[11px] text-neutral-500">{(ev.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                      <td className="px-3 py-2 font-mono text-neutral-900">{ev.event_type}</td>
                      <td className="px-3 py-2">{ev.organisation_name || '—'}</td>
                      <td className="px-3 py-2 text-neutral-700">{TIER_META[ev.tier]?.label || ev.tier || '—'}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-neutral-500">{ev.actor || 'system'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

const RebalanceCell = ({ wave, tier, row, onApply, busy, busyKey }) => {
  const [delta, setDelta] = useState(0);
  return (
    <div data-testid={`admin-rebalance-${wave}-${tier}`} className="border border-neutral-200 p-3">
      <div className="font-mono text-[11px] uppercase tracking-wider text-neutral-500">{TIER_META[tier]?.label || tier}</div>
      <div className="mt-1 text-sm">
        <NumberCell n={row.claimed_count} /> / <NumberCell n={row.public_capacity} />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <input type="number" value={delta} onChange={(e) => setDelta(Number(e.target.value || 0))}
               data-testid={`admin-rebalance-${wave}-${tier}-input`}
               className="w-20 border border-neutral-900 px-2 py-1 text-sm font-mono" />
        <button type="button" data-testid={`admin-rebalance-${wave}-${tier}-apply`}
                disabled={busy === busyKey || !delta} onClick={() => onApply(wave, tier, delta)}
                className="px-3 py-1.5 text-xs uppercase tracking-wider font-mono bg-neutral-900 text-white disabled:opacity-40">
          {busy === busyKey ? 'Working…' : 'Apply'}
        </button>
      </div>
    </div>
  );
};

export default AdminMemberships;
